import { useMutation, useQuery } from '@apollo/client';
import type { RenderParams } from '@seek/forms-ui';
import {
  Actions,
  Alert,
  Text,
  Button,
  Column,
  Columns,
  Heading,
  Stack,
  useToast,
  TextLink,
} from 'braid-design-system';
import { useState } from 'react';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import type { UserAccountType } from 'src/data';
import {
  GetUserDocument,
  UpdateUserDocument,
  type User,
} from 'src/generated/graphql';

import CountryField from './components/CountryField';
import DeleteButton from './components/DeleteButton';
import FirstNameField from './components/FirstNameField';
import LastNameField from './components/LastNameField';
import LocationField, {
  type AutosuggestValue,
} from './components/LocationField';
import PhoneNumberField from './components/PhoneNumberField';
import ResetPasswordButton from './components/ResetPasswordButton';
import { FormProvider, type FormValues } from './formState';

interface ProfileDetailsFormProps extends RenderParams<FormValues> {
  user: User;
  userAccountType: UserAccountType;
}

const ProfileDetailsForm = ({
  user,
  userAccountType,
  handleSubmit,
}: ProfileDetailsFormProps) => {
  const showToast = useToast();
  const [updateUser, updateUserResult] = useMutation(UpdateUserDocument, {
    refetchQueries: [{ query: GetUserDocument }],
  });
  const [location, setLocation] = useState<AutosuggestValue>({ text: '' });

  const submit = async (values: FormValues) => {
    try {
      await updateUser({
        variables: { input: { ...values, regionId: location.value ?? '' } },
      });

      showToast({
        tone: 'positive',
        message: 'User updated!',
        description: 'Thank you. Your details have been updated.',
        data: { testid: 'profile-update-success-toast' },
      });
    } catch {
      showToast({
        tone: 'critical',
        message: 'User update error',
        description:
          'Something went wrong while trying to update your details. Please try again.',
        data: { testid: 'profile-update-error-toast' },
      });
    }
    return {};
  };

  return (
    <form id="profile-details-form" onSubmit={handleSubmit(submit)}>
      <Stack space="xlarge">
        <Stack space="large">
          <Heading id="my-details-heading" level="3" component="h2">
            Personal details
          </Heading>

          <Stack space="medium">
            <Columns space="small">
              <Column>
                <FirstNameField />
              </Column>

              <Column>
                <LastNameField />
              </Column>
            </Columns>

            <Columns
              space={{ mobile: 'gutter', tablet: 'small' }}
              collapseBelow="tablet"
            >
              <Column>
                <CountryField />
              </Column>

              <Column>
                <PhoneNumberField />
              </Column>
            </Columns>

            <Columns space="small" collapseBelow="tablet">
              <Column width="1/2">
                <LocationField
                  initialValue={user.regionId ?? ''}
                  value={location}
                  onChange={setLocation}
                />
              </Column>
            </Columns>

            <Actions>
              <Button
                id="update-details-button"
                type="submit"
                loading={updateUserResult.loading}
                tone="formAccent"
              >
                Update details
              </Button>
            </Actions>
          </Stack>
        </Stack>

        <Stack space="medium">
          <Heading level="3" component="h2">
            Account management
          </Heading>

          <Actions>
            <ResetPasswordButton />
            {userAccountType === 'Buyer' && <DeleteButton />}
          </Actions>
        </Stack>
      </Stack>
    </form>
  );
};

interface ProfileDetailsProps {
  userAccountType: UserAccountType;
}

const ProfileDetails = ({ userAccountType }: ProfileDetailsProps) => {
  const { data, loading, error } = useQuery(GetUserDocument);

  const initialValues = {
    firstName: data?.user?.firstName ?? '',
    lastName: data?.user?.lastName ?? '',
    phoneNumber: data?.user?.phoneNumber ?? '',
    phoneCountryCode: data?.user?.phoneCountryCode ?? '61',
    regionId: data?.user?.regionId ?? '',
  };

  if (loading) return <CustomLoader />;
  if (error || !data)
    return (
      <Alert tone="critical">
        <Text>
          There was an error loading your profile. If this issue persists please{' '}
          <TextLink
            href="https://www.seekbusiness.com.au/contact-us"
            hitArea="large"
          >
            contact us
          </TextLink>
        </Text>
      </Alert>
    );

  return (
    <FormProvider initialValues={initialValues}>
      {(props) => (
        <ProfileDetailsForm
          {...props}
          user={initialValues}
          userAccountType={userAccountType}
        />
      )}
    </FormProvider>
  );
};

export default ProfileDetails;
